import React, { useState, useRef, useEffect } from 'react';

/**
 * An enhanced searchable select component with dropdown
 *
 * @param {Object} props
 * @param {Array} props.options - Array of option objects
 * @param {string} props.value - Current selected value (id)
 * @param {Function} props.onChange - Function called when selection changes
 * @param {string} props.placeholder - Placeholder text for the search input
 * @param {string} props.labelKey - Key in option object to use as display label (default: "label")
 * @param {string} props.valueKey - Key in option object to use as value (default: "id")
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.className - Additional classes for the container
 * @param {string} props.id - ID for the input element
 * @param {Function} props.renderOption - Custom render function for each option
 * @param {Function} props.renderSelectedOption - Custom render function for the selected option
 * @param {Function} props.filterFunction - Custom filter function for search
 */
const SearchableSelect = ({
                              options = [],
                              value = '',
                              onChange,
                              placeholder = 'Search...',
                              labelKey = 'label',
                              valueKey = 'id',
                              disabled = false,
                              className = '',
                              id = '',
                              renderOption = null,
                              renderSelectedOption = null,
                              filterFunction = null,
                          }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Find the selected option
    const selectedOption = options.find(option => option[valueKey] === value);

    // Default filter function if none provided
    const defaultFilterFunction = (option, term) =>
        option[labelKey].toLowerCase().includes(term.toLowerCase());

    // Use custom filter function or default
    const filterOptions = (term) => {
        if (filterFunction) {
            return options.filter(option => filterFunction(option, term));
        }
        return options.filter(option => defaultFilterFunction(option, term));
    };

    // Filter options based on search term
    const filteredOptions = filterOptions(searchTerm);

    const handleSelectOption = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                // Focus the input when opening
                setTimeout(() => {
                    inputRef.current && inputRef.current.focus();
                }, 0);
            }
        }
    };

    // Default render functions
    const defaultRenderOption = (option) => (
        <div className="px-4 py-2 text-sm">
            {option[labelKey]}
        </div>
    );

    const defaultRenderSelectedOption = (option) => (
        <span className="truncate">
      {option ? option[labelKey] : placeholder}
    </span>
    );

    return (
        <div
            className={`relative ${className}`}
            ref={dropdownRef}
        >
            {/* Selected value display */}
            <div
                className={`flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={toggleDropdown}
            >
                {renderSelectedOption
                 ? renderSelectedOption(selectedOption)
                 : defaultRenderSelectedOption(selectedOption)
                }
                <svg
                    className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                    />
                </svg>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-10 mt-1 w-full rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
                    <div className="sticky top-0 bg-white px-3 py-2 border-b">
                        <input
                            ref={inputRef}
                            id={id}
                            type="text"
                            value={searchTerm}
                            onChange={handleInputChange}
                            placeholder={placeholder}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="py-1">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <div
                                    key={option[valueKey]}
                                    className={`hover:bg-gray-100 cursor-pointer ${option[valueKey] === value ? 'bg-blue-50 text-blue-700' : ''}`}
                                    onClick={() => handleSelectOption(option[valueKey])}
                                >
                                    {renderOption
                                     ? renderOption(option)
                                     : defaultRenderOption(option)
                                    }
                                </div>
                            ))
                        ) : (
                             <div className="px-4 py-2 text-sm text-gray-500">No results found</div>
                         )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
